<script setup>
import { computed, ref, watch } from 'vue'
import { Button, FormField, Input, Modal, SegmentedControl, Select, Textarea, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { saveCapabilityModel } from '../../api/model-capabilities.js'
import {
  validateModelForm as validateTextModelForm,
  validatePricingForm as validateTextPricingForm,
} from './llm-model-form.js'
import {
  capabilityModelFormFromRow,
  capabilityModelSaveBody,
  newCapabilityModelForm,
  validateCapabilityModelForm,
} from './capability-model-form.js'
import PricingEditor from './provider/PricingEditor.vue'

const props = defineProps({
  open: { type: Boolean, required: true },
  capability: { type: String, required: true },
  catalog: { type: Object, required: true },
  modelRow: { type: Object, default: null },
})

const emit = defineEmits(['update:open', 'saved'])

const { t } = useI18n()
const toast = useToast()
const saving = ref(false)
const submitted = ref(false)
const form = ref(newCapabilityModelForm('image', ''))

const currentCapability = computed(() => props.catalog[props.capability])
const providersById = computed(() => new Map(props.catalog.providers.map(row => [row.id, row])))
const providerOptions = computed(() => currentCapability.value.provider_capabilities.map(providerCapability => {
  const provider = providersById.value.get(providerCapability.provider_id)
  return {
    value: providerCapability.id,
    label: `${provider.display_name} · ${providerCapability.code}`,
  }
}))
const statusOptions = computed(() => [
  { value: 'enabled', label: t('visualGameAdmin.llm.capabilities.status.enabled') },
  { value: 'disabled', label: t('visualGameAdmin.llm.capabilities.status.disabled') },
])
const status = computed({
  get: () => form.value.enabled ? 'enabled' : 'disabled',
  set: value => { form.value.enabled = value === 'enabled' },
})
const modelErrors = computed(() => (
  form.value.capability === 'text'
    ? validateTextModelForm(form.value)
    : validateCapabilityModelForm(form.value)
))
const textPricingErrors = computed(() => (
  form.value.capability === 'text' ? validateTextPricingForm(form.value) : {}
))

watch(() => props.open, (open) => {
  if (!open) return
  submitted.value = false
  form.value = props.modelRow === null
    ? newModelForm()
    : capabilityModelFormFromRow(props.capability, props.modelRow)
})

function newModelForm() {
  return newCapabilityModelForm(props.capability, providerOptions.value[0].value)
}

async function submit() {
  submitted.value = true
  if (Object.keys(modelErrors.value).length > 0) return
  saving.value = true
  try {
    const nextCatalog = await saveCapabilityModel(
      form.value.capability,
      capabilityModelSaveBody(form.value),
    )
    emit('saved', nextCatalog)
    emit('update:open', false)
  } catch (cause) {
    toast.error(cause.message, { duration: 0 })
  } finally {
    saving.value = false
  }
}

function modelError(field) {
  if (!submitted.value) return ''
  const code = modelErrors.value[field]
  return code ? t(`visualGameAdmin.llm.models.errors.${code}`) : ''
}

</script>

<template>
  <Modal
    :open="open"
    :title="t(`visualGameAdmin.llm.capabilities.modelForm.${modelRow ? 'edit' : 'create'}`)"
    :width="form.capability === 'text' ? '880px' : '560px'"
    @update:open="emit('update:open', $event)"
  >
    <form class="capability-model-form" @submit.prevent="submit">
      <FormField :label="t('visualGameAdmin.llm.capabilities.table.provider')" required>
        <Select
          v-model="form.provider_capability_id"
          :options="providerOptions"
          :disabled="modelRow !== null"
          block
        />
      </FormField>
      <FormField
        :label="t('visualGameAdmin.llm.capabilities.table.model')"
        :error="modelError('model')"
        required
      >
        <Input
          v-model="form.model"
          :disabled="modelRow !== null"
          :invalid="Boolean(modelError('model'))"
        />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.capabilities.table.status')">
        <SegmentedControl v-model="status" :options="statusOptions" />
      </FormField>
      <FormField
        v-if="form.capability === 'image'"
        :label="t('visualGameAdmin.llm.capabilities.modelForm.imagePrice')"
        :error="modelError('image_price')"
        required
      >
        <Input v-model="form.image_price" type="number" :invalid="Boolean(modelError('image_price'))" />
      </FormField>
      <template v-if="form.capability === 'video'">
        <FormField :label="t('visualGameAdmin.llm.capabilities.modelForm.videoWithInputPrice')" :error="modelError('video_with_input_price')" required>
          <Input v-model="form.video_with_input_price" type="number" :invalid="Boolean(modelError('video_with_input_price'))" />
        </FormField>
        <FormField :label="t('visualGameAdmin.llm.capabilities.modelForm.videoWithoutInputPrice')" :error="modelError('video_without_input_price')" required>
          <Input v-model="form.video_without_input_price" type="number" :invalid="Boolean(modelError('video_without_input_price'))" />
        </FormField>
      </template>
      <FormField
        v-if="form.capability === 'tts'"
        :label="t('visualGameAdmin.llm.capabilities.modelForm.ttsPrice')"
        :error="modelError('tts_price')"
        required
      >
        <Input v-model="form.tts_price" type="number" :invalid="Boolean(modelError('tts_price'))" />
      </FormField>
      <template v-if="form.capability === 'embedding'">
        <FormField
          :label="t('visualGameAdmin.llm.capabilities.modelForm.dimensions')"
          :hint="t('visualGameAdmin.llm.capabilities.modelForm.dimensionsHint')"
          :error="modelError('dimensions')"
          required
        >
          <Input
            v-model="form.dimensions"
            type="number"
            :disabled="modelRow !== null"
            :invalid="Boolean(modelError('dimensions'))"
          />
        </FormField>
        <FormField
          :label="t('visualGameAdmin.llm.capabilities.modelForm.embeddingPrice')"
          :error="modelError('embedding_price')"
          required
        >
          <Input v-model="form.embedding_price" type="number" :invalid="Boolean(modelError('embedding_price'))" />
        </FormField>
      </template>
      <PricingEditor
        v-if="form.capability === 'text'"
        v-model="form"
        class="capability-model-form__wide"
        :errors="textPricingErrors"
        :show-errors="submitted"
      />
      <FormField
        class="capability-model-form__wide"
        :label="t('visualGameAdmin.llm.models.form.params')"
        :error="modelError('params')"
      >
        <Textarea
          v-model="form.params"
          :rows="3"
          :invalid="Boolean(modelError('params'))"
        />
      </FormField>
    </form>
    <template #footer>
      <Button variant="ghost" @click="emit('update:open', false)">{{ t('visualGameAdmin.common.cancel') }}</Button>
      <Button variant="primary" :loading="saving" @click="submit">{{ t('visualGameAdmin.common.save') }}</Button>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.capability-model-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--drama-space-lg);
  min-width: min(520px, 76vw);

  &__wide {
    grid-column: 1 / -1;
  }
}

@media (max-width: 760px) {
  .capability-model-form {
    grid-template-columns: 1fr;
    min-width: 0;
  }
}
</style>
